<script lang="ts">
  import { createEventDispatcher, onMount } from "svelte";
  import { Button, Input, Spinner, Label } from "flowbite-svelte";
  import { project } from "$lib/services/store";
  import Icon from "@iconify/svelte";
  import type { Operator } from "@/src/types/components/operators/Operator";
  import { getOperatorLogo } from "$lib/common/helpers/image";
  import { fetchOperators, filterOperatorsBySearchTerm } from "$lib/data/queries/operators";

  export let operatorId: number | null = null;
  export let required: boolean = false;
  export let label: string = "Operator";
  export let showLabel: boolean = true;
  export let autoLoad: boolean = true;
  export let fieldName: string | null = null;

  let operators: Operator[] = [];
  let filteredOperators: Operator[] = [];
  let isLoading: boolean = false;
  let error: string | null = null;
  let searchTerm: string = "";

  const dispatch = createEventDispatcher<{
    select: number | null;
    change: { field: string; value: number | null };
  }>();

  $: {
    filteredOperators = filterOperatorsBySearchTerm(operators, searchTerm);
  }

  onMount(() => {
    if (autoLoad) {
      loadOperators();
    }
  });

  async function loadOperators() {
    isLoading = true;
    error = null;

    try {
      const market = $project?.settings?.market || "se";
      const result = await fetchOperators(market);

      operators = result.operators;
      error = result.error;
    } finally {
      isLoading = false;
    }
  }

  function selectOperator(id: number) {
    operatorId = id;
    dispatch("select", id);
    if (fieldName) {
      dispatch("change", { field: fieldName, value: id });
    }
  }

  function clearOperator() {
    operatorId = null;
    dispatch("select", null);
    if (fieldName) {
      dispatch("change", { field: fieldName, value: null });
    }
  }

  export { loadOperators };
</script>

<div>
  {#if showLabel}
    <Label for="operator-search" class="mb-2">
      {label}
      {#if required}<span class="text-red-500">*</span>{/if}
    </Label>
  {/if}

  {#if isLoading}
    <div class="flex justify-center items-center py-4">
      <Spinner size="6" />
      <span class="ml-2">Loading operators...</span>
    </div>
  {:else if error}
    <div class="p-3 bg-red-50 text-red-700 rounded border border-red-200 mb-4">
      <p class="text-sm">{error}</p>
      <Button size="xs" color="red" class="mt-2" on:click={loadOperators}>Retry</Button>
    </div>
  {:else if operatorId}
    <div class="p-3 bg-gray-50 dark:bg-gray-800 border rounded dark:border-gray-700">
      <h5 class="font-medium text-gray-900 dark:text-white mb-1">Selected Operator:</h5>
      {#if isLoading}
        <div class="text-sm text-gray-500 dark:text-gray-400">Loading operator details...</div>
      {:else if operators.find((op) => op.id === operatorId)}
        <div class="flex flex-col gap-4">
          <div class="w-full flex items-center gap-4">
            <div>
            <span class="text-sm font-medium text-gray-900 dark:text-white">
              {operators.find((op) => op.id === operatorId)?.name}
            </span>
            <span class="text-xs text-gray-500 dark:text-gray-400 ml-1">
              (ID: {operatorId})
            </span>
          </div>

            <a
              href={`/admin/tools/operators/${operatorId}`}
              class="align-self-end text-xs text-gray-500 dark:text-gray-400"
              target="_blank">
              <Icon icon="heroicons:arrow-top-right-on-square" class="w-4 h-4 inline-block" />
            </a>
          </div>
          <div class="w-full flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-md h-20 p-2">
            <img
              src={getOperatorLogo($project, operators.find((op) => op.id === operatorId)?.logo)}
              alt="Operator logo"
              class="h-full" />
          </div>
          <Button size="xs" color="light" on:click={clearOperator}>
            <Icon icon="heroicons:arrow-path-solid" class="w-4 h-4 inline-block mr-2" />
            Change Operator</Button>
        </div>
      {:else}
        <div class="text-sm text-yellow-600 dark:text-yellow-400">
          Operator with ID {operatorId} not found in current market ({$project.settings.market}).
        </div>
      {/if}
    </div>
  {:else}
    <div class="mb-3">
      <div class="relative">
        <Input id="operator-search" placeholder="Search operators..." bind:value={searchTerm} />
        {#if isLoading}
          <div class="absolute right-3 top-2">
            <div class="animate-spin h-5 w-5 border-2 border-blue-500 rounded-full border-t-transparent"></div>
          </div>
        {/if}
      </div>
    </div>

    <div class="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg mb-4">
      {#if filteredOperators.length === 0}
        <div class="p-3 text-center text-gray-500 dark:text-gray-400">
          {searchTerm ? "No operators match your search" : "No operators available"}
        </div>
      {:else}
        <div class="divide-y dark:divide-gray-700">
          {#each filteredOperators as op (op.id)}
            <button
              type="button"
              class="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              class:bg-blue-50={operatorId === op.id}
              class:dark:bg-blue-900={operatorId === op.id}
              on:click={() => selectOperator(op.id)}>
              <div class="font-medium text-gray-900 dark:text-white">
                {op.name}
              </div>
              <div class="text-xs text-gray-500 dark:text-gray-400">
                ID: {op.id}
              </div>
            </button>
          {/each}
        </div>
      {/if}
    </div>
  {/if}
</div>
